import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie, Shield, Settings } from 'lucide-react';

interface CookiePreferences {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
}

const COOKIE_CONSENT_KEY = 'paircraft_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'paircraft_cookie_preferences';

const CookieConsent: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [preferences, setPreferences] = useState<CookiePreferences>({
        necessary: true, // Always required
        analytics: true,
        marketing: false,
    });

    useEffect(() => {
        // Check if user has already consented
        const hasConsented = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (!hasConsented) {
            // Small delay before showing to avoid layout shift on page load
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAcceptAll = () => {
        const allAccepted: CookiePreferences = {
            necessary: true,
            analytics: true,
            marketing: true,
        };
        saveConsent(allAccepted);
    };

    const handleAcceptNecessary = () => {
        const necessaryOnly: CookiePreferences = {
            necessary: true,
            analytics: false,
            marketing: false,
        };
        saveConsent(necessaryOnly);
    };

    const handleSavePreferences = () => {
        saveConsent(preferences);
    };

    const saveConsent = (prefs: CookiePreferences) => {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
        localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
        setIsVisible(false);

        // Dispatch event for other parts of the app to listen to
        window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: prefs }));
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                    {/* Main Banner */}
                    {!showPreferences ? (
                        <div className="p-6">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                                    <Cookie className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        We value your privacy 🍪
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        We use cookies to enhance your browsing experience, provide personalized content,
                                        and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
                                        Read our{' '}
                                        <Link to="/cookies" className="text-blue-600 hover:text-blue-700 underline">
                                            Cookie Policy
                                        </Link>{' '}
                                        to learn more.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button
                                            onClick={handleAcceptAll}
                                            className="px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                                        >
                                            Accept All
                                        </button>
                                        <button
                                            onClick={handleAcceptNecessary}
                                            className="px-5 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                                        >
                                            Necessary Only
                                        </button>
                                        <button
                                            onClick={() => setShowPreferences(true)}
                                            className="px-5 py-2.5 text-gray-600 text-sm font-medium hover:text-gray-900 transition-colors inline-flex items-center gap-2"
                                        >
                                            <Settings className="w-4 h-4" />
                                            Customize
                                        </button>
                                    </div>
                                </div>
                                <button
                                    onClick={handleAcceptNecessary}
                                    className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                    aria-label="Close"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Preferences Panel */
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                                        <Shield className="w-5 h-5 text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900">Cookie Preferences</h3>
                                </div>
                                <button
                                    onClick={() => setShowPreferences(false)}
                                    className="text-sm text-gray-500 hover:text-gray-700"
                                >
                                    ← Back
                                </button>
                            </div>

                            <div className="space-y-4 mb-6">
                                {/* Necessary Cookies */}
                                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h4 className="font-medium text-gray-900">Necessary Cookies</h4>
                                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                                                Required
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600">
                                            Essential for the website to function properly. These cannot be disabled.
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0 ml-4">
                                        <div className="w-12 h-6 bg-green-500 rounded-full relative cursor-not-allowed">
                                            <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow" />
                                        </div>
                                    </div>
                                </div>

                                {/* Analytics Cookies */}
                                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                                    <div className="flex-1">
                                        <h4 className="font-medium text-gray-900 mb-1">Analytics Cookies</h4>
                                        <p className="text-sm text-gray-600">
                                            Help us understand how visitors interact with our website to improve user experience.
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0 ml-4">
                                        <button
                                            onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
                                            className={`w-12 h-6 rounded-full relative transition-colors ${preferences.analytics ? 'bg-blue-500' : 'bg-gray-300'
                                                }`}
                                        >
                                            <div
                                                className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${preferences.analytics ? 'right-1' : 'left-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>
                                </div>

                                {/* Marketing Cookies */}
                                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                                    <div className="flex-1">
                                        <h4 className="font-medium text-gray-900 mb-1">Marketing Cookies</h4>
                                        <p className="text-sm text-gray-600">
                                            Used to deliver personalized advertisements and track ad campaign performance.
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0 ml-4">
                                        <button
                                            onClick={() => setPreferences(prev => ({ ...prev, marketing: !prev.marketing }))}
                                            className={`w-12 h-6 rounded-full relative transition-colors ${preferences.marketing ? 'bg-blue-500' : 'bg-gray-300'
                                                }`}
                                        >
                                            <div
                                                className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${preferences.marketing ? 'right-1' : 'left-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                <Link
                                    to="/cookies"
                                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                                >
                                    View Cookie Policy
                                </Link>
                                <button
                                    onClick={handleSavePreferences}
                                    className="px-6 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                                >
                                    Save Preferences
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Animation styles */}
            <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
        </div>
    );
};

export default CookieConsent;
